import React, { createContext, useContext, useState, useEffect } from 'react';
import api from '../utils/apiClient';
import { useAuth } from './AuthContext';

const ProjectContext = createContext();
export const useProjects = () => useContext(ProjectContext);

export const ProjectProvider = ({ children }) => {
  const { user } = useAuth();
  const [projects, setProjects] = useState([]);
  const [currentProject, setCurrentProject] = useState(null);
  const [loading, setLoading] = useState(true);
  const [isCurrentProjectOnboarded, setIsCurrentProjectOnboarded] = useState(null);
  const [error, setError] = useState(null);

  // on user change, reload list + current
  useEffect(() => {
    if (!user) {
      setProjects([]);
      setCurrentProject(null);
      setIsCurrentProjectOnboarded(false);
      setLoading(false);
      return;
    }

    const abortController = new AbortController();
    const { signal } = abortController;

    const initializeProjects = async () => {
      setLoading(true);

      // On page reload, check if we have cached project data first
      try {
        // First check if we have a currentProjectId in localStorage
        const stored = localStorage.getItem('currentProjectId');
        if (stored) {
          // Check if we have a cached onboarding status
          const cachedOnboardingStatus = localStorage.getItem('isCurrentProjectOnboarded');
          if (cachedOnboardingStatus !== null) {
            // Use cached status initially to prevent flashing
            setIsCurrentProjectOnboarded(JSON.parse(cachedOnboardingStatus));
          }

          // Check if we have cached project data
          const cachedProject = localStorage.getItem('cachedProjectData');
          if (cachedProject) {
            const projectData = JSON.parse(cachedProject);
            setCurrentProject(projectData);

            // Use cached project data, but still fetch in background
            const fetchInBackground = async () => {
              try {
                const listRes = await api.projects.list({ signal });
                if (signal.aborted) return;
                setProjects(listRes.data);

                const res = await api.projects.get(stored, { signal });
                if (signal.aborted) return;

                // Update cache and state
                localStorage.setItem('cachedProjectData', JSON.stringify(res.data));
                setCurrentProject(res.data);

                const onboardingStatus = calculateProjectOnboardingStatus(res.data);
                localStorage.setItem('isCurrentProjectOnboarded', JSON.stringify(onboardingStatus));

                setTimeout(() => {
                  if (!signal.aborted) {
                    setIsCurrentProjectOnboarded(onboardingStatus);
                  }
                }, 2000);
              } catch (err) {
                if (err?.code === 'ERR_CANCELED' || err?.name === 'CanceledError' || signal.aborted) {
                  return;
                }
                console.error('Background data fetch error:', err);
              } finally {
                if (!signal.aborted) {
                  setLoading(false);
                }
              }
            };

            fetchInBackground();
            return;
          }
        }

        // If no cached data, proceed with normal loading sequence
        const projectsRes = await api.projects.list({ signal });
        if (signal.aborted) return;
        setProjects(projectsRes.data);

        if (projectsRes.data && projectsRes.data.length > 0) {
          const projectId = stored || projectsRes.data[0].id;
          const projectRes = await api.projects.get(projectId, { signal });
          if (signal.aborted) return;

          localStorage.setItem('currentProjectId', projectId);
          localStorage.setItem('cachedProjectData', JSON.stringify(projectRes.data));
          setCurrentProject(projectRes.data);

          const onboardingStatus = calculateProjectOnboardingStatus(projectRes.data);
          localStorage.setItem('isCurrentProjectOnboarded', JSON.stringify(onboardingStatus));
          setIsCurrentProjectOnboarded(onboardingStatus);
        }
      } catch (error) {
        if (error?.code === 'ERR_CANCELED' || error?.name === 'CanceledError' || signal.aborted) {
          return;
        }
        console.error('Error initializing projects:', error);
      } finally {
        if (!signal.aborted) {
          setLoading(false);
        }
      }
    };

    initializeProjects();
    return () => abortController.abort();
  }, [user]);

  // load all projects
  const loadProjects = async () => {
    if (!user) return;
    setLoading(true);
    try {
      const res = await api.projects.list();
      setProjects(res.data);

      // If we have projects but no current project selected, select the first one
      if (res.data && res.data.length > 0 && !currentProject) {
        const projectId = localStorage.getItem('currentProjectId') || res.data[0].id;
        await loadCurrentProject(projectId);
      }
    } catch (err) {
      console.error('loadProjects error:', err);
      setError(err.response?.data?.error || err.message);
    } finally {
      setLoading(false);
    }
  };

  // load one project by ID
  const loadCurrentProject = async (projectId, options = {}) => {
    if (!projectId) {
      setCurrentProject(null);
      setIsCurrentProjectOnboarded(false);
      setLoading(false);
      return;
    }

    setLoading(true);
    try {
      // Skip refetch only when explicitly not forcing and we already hold this project in memory
      const currentCachedId = localStorage.getItem('currentProjectId');
      if (
        !options.force &&
        currentCachedId === projectId &&
        currentProject &&
        currentProject.id === projectId
      ) {
        setLoading(false);
        return;
      }

      const res = await api.projects.get(projectId);
      setCurrentProject(res.data);
      localStorage.setItem('currentProjectId', projectId);
      localStorage.setItem('cachedProjectData', JSON.stringify(res.data));

      // Skip updating onboarding status if preventRedirect is true
      if (options.preventRedirect) {
        // Maintain the current onboarding status
        setLoading(false);
        return;
      }

      const onboardingStatus = calculateProjectOnboardingStatus(res.data);

      // On page reload, temporarily maintain previous onboarding status
      // This prevents flashing to onboarding page
      if (window.performance && window.performance.navigation.type === window.performance.navigation.TYPE_RELOAD) {
        // Get the previously stored onboarding status
        const previousStatus = JSON.parse(localStorage.getItem('isCurrentProjectOnboarded'));

        // If previously onboarded, maintain that status briefly
        if (previousStatus === true) {
          // Set the current status immediately (this will keep user on dashboard)
          setIsCurrentProjectOnboarded(true);

          // Only update with actual status after a delay
          setTimeout(() => {
            setIsCurrentProjectOnboarded(onboardingStatus);
            // Store the real status for next reload
            localStorage.setItem('isCurrentProjectOnboarded', JSON.stringify(onboardingStatus));
          }, 2500);
        } else {
          // If not previously onboarded, just set the actual status
          setIsCurrentProjectOnboarded(onboardingStatus);
          localStorage.setItem('isCurrentProjectOnboarded', JSON.stringify(onboardingStatus));
        }
      } else {
        // Normal navigation, just set the status
        setIsCurrentProjectOnboarded(onboardingStatus);
        localStorage.setItem('isCurrentProjectOnboarded', JSON.stringify(onboardingStatus));
      }
    } catch (err) {
      console.error('loadCurrentProject error:', err);
      setError(err.response?.data?.error || err.message);
      setIsCurrentProjectOnboarded(false);
    } finally {
      setLoading(false);
    }
  };

  const calculateProjectOnboardingStatus = (p) => {
    if (!p || p == null) return false;

    // 1) business step
    if (!p.business_name || !p.website || !p.description) return false;
    if (!Array.isArray(p.target_audiences) || p.target_audiences.length === 0)
      return false;

    // 2) competitors (at least one)
    if (!Array.isArray(p.project_competitors) || p.project_competitors.length === 0)
      return false;

    // 3) blog details exist
    const bd = p.blog_details;
    if (
      !bd ||
      !bd.sitemap_url ||
      !bd.blog_root_url ||
      !Array.isArray(bd.best_article_examples) ||
      bd.best_article_examples.length === 0
    ) return false;

    // 4) blogging style
    if (
      !p.blog_language ||
      !p.blog_tone ||
      typeof p.suggested_internal_links !== 'number' ||
      p.add_youtube_video == null ||
      p.add_cta == null ||
      !p.image_style
    ) return false;

    // 5) keyword region + keywords
    if (!p.keyword_region) return false;
    if (!Array.isArray(p.project_keywords) || p.project_keywords.length === 0)
      return false;

    return true;
  };

  // let components switch current project
  const selectProject = (projectId) => {
    loadCurrentProject(projectId);
  };

  return (
    <ProjectContext.Provider
      value={{
        projects,
        currentProject,
        loading,
        error,
        loadProjects,
        loadCurrentProject,
        selectProject,
        isCurrentProjectOnboarded,
        setCurrentProject,
      }}
    >
      {children}
    </ProjectContext.Provider>
  );
};
